/* eslint-disable */
import { Form, notification, Spin, Button } from 'antd';
import React, { useState, useEffect } from 'react';
import TextArea from 'antd/es/input/TextArea';
import { RefetchOptions, QueryObserverResult, UseMutationResult } from '@tanstack/react-query';
import { LoadingOutlined } from '@ant-design/icons';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { User } from '@/types/TAccounts';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent } from '@/store/account/selector';
import { getAllVariables } from '@/store/novi/variables/selector';
import { setVariable } from '@/store/novi/variables/slice';
import { Variable } from '@/types/TVariables';
import TextEditor from '@/components/TextEditor/TextEditor';
import { updateCampaignVariables } from '@/api/novi/CamapaignPortalApis';
import { useMutation } from '@tanstack/react-query';
import { getSelectedAgentId } from '@/store/account/selector';
import { safeJSONParse } from '@/utils/helper';

export const AiPromptForm: React.FC<{
	ai_prompt?: string;
	refetch: ((options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>) | (() => void);
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	isNoviUser?: boolean;
	onCancel?: () => void;
}> = ({ ai_prompt, mutation, refetch, isNoviUser, onCancel }) => {
	const [form] = Form.useForm<{ ai_prompt: string }>();
	const dispatch = useAppDispatch();
	const [formTouched, setFormTouched] = useState<boolean>(false);
	const variables = useAppSelector(getAllVariables);
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	let selectedCampaign: any;
	if (isNoviUser) {
		selectedCampaign = useAppSelector(getSelectedAgent);
	}

	useEffect(() => {
		if (selectedCampaign?.variables?.length) {
			try {
				const parsedVariables = safeJSONParse(selectedCampaign.variables, {});
				Object.entries(parsedVariables).forEach(([key, value]) => {
					dispatch(setVariable({ name: key, data: value as Variable }));
				});
			} catch (error) {
				console.error('Error setting variables:', error);
			}
		}
	}, [selectedCampaign, dispatch]);

	const handleSubmit = async (value: any) => {
		await mutation.mutateAsync({
			ai_prompt: value.ai_prompt,
			agentId: selectedAgentId as string,
		});

		notification.success({
			key: 'form-success',
			message: 'AI Prompt has been saved successfully!',
			duration: 3,
		});
		setFormTouched(false);
		refetch();
		if (onCancel) {
			onCancel();
		}
	};

	const { mutate: updateVariables } = useMutation({
		mutationKey: ['update-variables'],
		mutationFn: () => updateCampaignVariables(selectedCampaign?.number, variables, 'Client'),
		onSuccess: () => {
			notification.success({
				key: 'form-success',
				message: 'Variables have been saved successfully!',
				duration: 3,
			});
			setFormTouched(false);
			refetch();
		},
	});

	const handleVariableChange = (updatedVariables: any) => {
		Object.entries(updatedVariables).forEach(([key, value]) => {
			dispatch(setVariable({ name: key, data: value as Variable }));
		});

		updateVariables();
	};

	const handleValuesChange = () => {
		setFormTouched(true);
		form.validateFields();
	};

	return (
		<>
			<Spin
				spinning={mutation.isPending}
				indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
			>
				<Form
					form={form}
					layout="vertical"
					initialValues={{
						ai_prompt,
					}}
					onFinish={handleSubmit}
					onValuesChange={handleValuesChange}
				>
					<Form.Item name="ai_prompt" className="!mb-4">
						{isNoviUser ? (
							<TextEditor
								placeholder="Please enter your AI Prompt"
								variables={variables}
								onChange={(_, variables) => {
									if (variables) {
										handleVariableChange(variables);
									}
								}}
							/>
						) : (
							<TextArea
								autoSize={{ minRows: 3, maxRows: 12 }}
								placeholder="Please enter your AI Prompt"
							/>
						)}
					</Form.Item>

					<div className="flex justify-between mt-6">
						<Button
							onClick={() => {
								form.resetFields();
								setFormTouched(false);
								if (onCancel) {
									onCancel();
								}
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							disabled={!formTouched}
							className="bg-blue-500 hover:bg-blue-600"
						>
							Update
						</Button>
					</div>
				</Form>
			</Spin>
		</>
	);
};
